import { useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ApiError, AUTHORIZE_PARAMS, appAuthApi, post, type Consent } from '../api'
import { useSession } from '../session'
import { t } from '../i18n'
import { Button, Field, Notice } from '../components/ui'
import { Shell } from './Auth'

/*
 * Signing an app in from the browser (docs/bridge-auth.md §3, §4): the consent
 * screen of the loopback flow (/oauth/authorize) and the code page of the
 * device flow (/device). Both need a signed-in user (RequireUser keeps the
 * query through /login); neither ever redirects by itself — only Allow or
 * Cancel goes back to the app, and only to the loopback address the center
 * returned.
 */

/** Only the loopback addresses of RFC 8252 §7.3 (the center checks the same; this is the second lock). */
export function isLoopback(url: string): boolean {
  try {
    const u = new URL(url)
    return u.protocol === 'http:' && (u.hostname === '127.0.0.1' || u.hostname === '[::1]') && u.pathname === '/callback'
  } catch {
    return false
  }
}

const SCOPE_TEXT: Record<string, Parameters<typeof t>[0]> = {
  bridge: 'appAuth.scope.bridge',
  outputs: 'appAuth.scope.outputs',
  'stream_keys:read': 'appAuth.scope.streamKeys',
}

/** What the app asks for, the account and the device. */
function ConsentBody({ c }: { c: Consent }) {
  const { user } = useSession()
  const nav = useNavigate()
  const loc = useLocation()
  const qc = useQueryClient()
  const scopes = c.scopes.filter((s) => SCOPE_TEXT[s])
  return (
    <div className="space-y-4 text-sm">
      <p className="text-base text-text">{t('appAuth.wants', { app: c.client_name })}</p>
      {c.device_name && (
        <p className="text-text-soft" data-testid="consent-device">
          {t('appAuth.on', { device: c.device_name })}
        </p>
      )}
      {scopes.length > 0 && (
        <div>
          <p className="mb-1 text-text-soft">{t('appAuth.can')}</p>
          <ul className="list-disc space-y-1 pl-5 text-text" data-testid="consent-scopes">
            {scopes.map((s) => (
              <li key={s}>{t(SCOPE_TEXT[s]!)}</li>
            ))}
          </ul>
        </div>
      )}
      {c.scopes.includes('stream_keys:read') && (
        <p className="text-text" data-testid="consent-keys-warning">
          {t('appAuth.keysWarning')}
        </p>
      )}
      <p className="text-muted">
        {t(c.scopes.includes('stream_keys:read') ? 'appAuth.cannotWithKeys' : 'appAuth.cannot')}
      </p>
      <p className="border-t border-line pt-3 text-muted">
        {t('appAuth.as', { email: user?.email ?? '' })}
        <br />
        <button
          type="button"
          className="text-text underline underline-offset-4"
          onClick={async () => {
            await post('/api/auth/logout').catch(() => undefined)
            qc.clear()
            nav('/login', { replace: true, state: { from: loc.pathname + loc.search } })
            location.reload()
          }}
        >
          {t('appAuth.notYou')}
        </button>
      </p>
    </div>
  )
}

/** /oauth/authorize: the consent screen of the loopback flow. */
export function OAuthAuthorize() {
  const [search] = useSearchParams()
  const [consent, setConsent] = useState<Consent | null>(null)
  const [bad, setBad] = useState(false)
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null)
  const [back, setBack] = useState(false)
  // only the parameters the center knows, in a stable order
  const params: Record<string, string> = {}
  for (const k of AUTHORIZE_PARAMS) {
    const v = search.get(k)
    if (v !== null) params[k] = v
  }
  const query = new URLSearchParams(params).toString()

  useEffect(() => {
    let live = true
    setConsent(null)
    setBad(false)
    appAuthApi
      .consent(new URLSearchParams(query))
      .then((c) => live && setConsent(c))
      .catch(() => live && setBad(true))
    return () => {
      live = false
    }
  }, [query])

  const decide = async (d: 'allow' | 'deny') => {
    setBusy(d)
    try {
      const r = await appAuthApi.decide(params, d)
      if (!isLoopback(r.redirect)) throw new Error('not a loopback address')
      setBack(true)
      location.assign(r.redirect)
    } catch {
      setBad(true)
    } finally {
      setBusy(null)
    }
  }

  const app = consent?.client_name ?? 'the app'
  return (
    <Shell title={consent ? t('appAuth.title', { app }) : t('appAuth.title', { app: 'an app' })}>
      {bad ? (
        <Notice>{t('appAuth.bad')}</Notice>
      ) : back ? (
        <div className="space-y-2 text-sm" role="status" data-testid="consent-back">
          <p className="text-text">{t('appAuth.back', { app })}</p>
          <p className="text-muted">{t('appAuth.backHint', { app })}</p>
        </div>
      ) : !consent ? (
        <p className="text-sm text-muted">{t('appAuth.loading')}</p>
      ) : (
        <>
          <ConsentBody c={consent} />
          <div className="mt-5 flex gap-2">
            <Button variant="primary" className="flex-1 py-2" disabled={!!busy} onClick={() => decide('allow')}>
              {t('appAuth.allow')}
            </Button>
            <Button className="flex-1 py-2" disabled={!!busy} onClick={() => decide('deny')}>
              {t('appAuth.cancel')}
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted">{t('appAuth.revokeHint')}</p>
        </>
      )}
    </Shell>
  )
}

/** XXXX-XXXX from whatever was typed (the center accepts either; this is for the eye). */
export function formatUserCode(v: string): string {
  const s = v.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 8)
  return s.length > 4 ? `${s.slice(0, 4)}-${s.slice(4)}` : s
}

/** /device: the code page of the device flow (RFC 8628 §3.3). */
export function DeviceLink() {
  const [search, setSearch] = useSearchParams()
  const [code, setCode] = useState(formatUserCode(search.get('code') ?? ''))
  const [consent, setConsent] = useState<Consent | null>(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<'allow' | 'deny' | null>(null)

  const check = async (e?: FormEvent) => {
    e?.preventDefault()
    setBusy(true)
    setErr('')
    try {
      setConsent(await appAuthApi.deviceConsent(code))
    } catch (x) {
      setErr(x instanceof ApiError && x.status === 429 ? t('device.tooMany') : t('device.badCode'))
    } finally {
      setBusy(false)
    }
  }

  const decide = async (d: 'allow' | 'deny') => {
    setBusy(true)
    setErr('')
    try {
      await appAuthApi.deviceDecide(code, d)
      setDone(d)
    } catch {
      setConsent(null)
      setErr(t('device.badCode'))
    } finally {
      setBusy(false)
    }
  }

  const reset = () => {
    setConsent(null)
    setDone(null)
    setCode('')
    setErr('')
    if (search.has('code')) setSearch({}, { replace: true })
  }

  const app = consent?.client_name ?? 'the app'
  if (done)
    return (
      <Shell title={t('device.title')}>
        <p role="status" className="text-sm text-text" data-testid="device-done">
          {done === 'allow' ? t('device.allowed', { app }) : t('device.denied')}
        </p>
        {done === 'allow' && <p className="mt-2 text-xs text-muted">{t('appAuth.revokeHint')}</p>}
        <p className="mt-5 flex gap-4 text-sm">
          <button type="button" className="text-text underline underline-offset-4" onClick={reset}>
            {t('device.another')}
          </button>
          <Link to="/account/devices" className="text-text underline underline-offset-4">
            {t('account.devices')}
          </Link>
        </p>
      </Shell>
    )

  if (consent)
    return (
      <Shell title={t('appAuth.title', { app })}>
        <p className="mb-4 rounded-md border border-warn/50 bg-warn/[.08] px-3 py-2 text-sm text-text" data-testid="device-warn">
          {t('device.warn', { app })}
        </p>
        <p className="mb-4 font-mono text-lg tracking-widest text-text" data-testid="device-code">
          {code}
        </p>
        <ConsentBody c={consent} />
        <Notice>{err}</Notice>
        <div className="mt-5 flex gap-2">
          <Button variant="primary" className="flex-1 py-2" disabled={busy} onClick={() => decide('allow')}>
            {t('appAuth.allow')}
          </Button>
          <Button className="flex-1 py-2" disabled={busy} onClick={() => decide('deny')}>
            {t('appAuth.cancel')}
          </Button>
        </div>
      </Shell>
    )

  return (
    <Shell title={t('device.title')}>
      <form onSubmit={check} className="space-y-4">
        <p className="text-sm text-text-soft">{t('device.intro')}</p>
        <Field
          label={t('device.code')}
          name="user_code"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          placeholder="XXXX-XXXX"
          value={code}
          onChange={(e) => setCode(formatUserCode(e.target.value))}
          required
        />
        <Notice>{err}</Notice>
        <Button variant="primary" type="submit" disabled={busy || code.replace('-', '').length !== 8} className="w-full py-2">
          {t('device.next')}
        </Button>
      </form>
    </Shell>
  )
}
